<template>
    <div class="relative">
        <Button
            class="text-z-primary-600"
            variant="ghost"
            @click="expanded = !expanded"
        >
            <Plus stroke-width="1.75" :size="20"></Plus>
            {{ $t("event_types.booking_form.add_question") }}
        </Button>

        <Transition>
            <div
                v-if="expanded"
                ref="target"
                class="absolute left-6 mt-2 flex-col rounded-lg border border-iron-200 bg-white p-3 shadow-lg"
            >
                <ul class="space-y-2">
                    <li
                        v-for="(question, i) of questions"
                        :key="i"
                        class="rounded-lg transition-all duration-100 ease-out hover:bg-iron-100"
                    >
                        <button
                            class="flex w-full items-center gap-3 px-3 py-[10px]"
                            @click="addElement(question)"
                        >
                            <div class="shrink-0">
                                <component
                                    :is="question.icon"
                                    :size="20"
                                    stroke-width="1.75"
                                    class="text-iron-500"
                                />
                            </div>

                            <div class="flex flex-col items-start pr-4">
                                <p
                                    class="text-left text-sm font-medium text-iron-700"
                                >
                                    {{ question.title }}
                                </p>
                                <p class="text-left text-sm text-iron-500">
                                    {{ question.description }}
                                </p>
                            </div>
                        </button>
                    </li>
                </ul>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import {
    Type,
    PhoneCall,
    CheckSquare,
    List,
    Text,
    CircleDot,
    ListChecks,
} from "lucide-vue-next";
import { onClickOutside } from "@vueuse/core";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-vue-next";
import { useI18n } from "vue-i18n";

const expanded = ref(false);

const target = ref();

const props = defineProps<{
    hasFirstname: boolean;
    hasLastname: boolean;
    hasPhone: boolean;
}>();

const { t } = useI18n();

onClickOutside(target, () => {
    expanded.value = false;
});

const emit = defineEmits(["add-custom-field"]);

export type Question = {
    icon: any;
    title: string;
    description: string;
    predefined?: "first_name" | "last_name" | "phone";
    type:
        | "text"
        | "email"
        | "phone"
        | "number"
        | "textarea"
        | "checkbox"
        | "select"
        | "multiselect"
        | "radio";
};

watch(
    () => props.hasFirstname,
    () => {
        if (!props.hasFirstname) {
            questions.value.unshift({
                type: "text",
                icon: Type,
                title: t("event_types.booking_form.questions.first_name.title"),
                description: t(
                    "event_types.booking_form.questions.first_name.description",
                ),
                predefined: "first_name",
            });
        } else {
            questions.value = questions.value.filter(
                (question) => question.predefined !== "first_name",
            );
        }
    },
);

watch(
    () => props.hasLastname,
    () => {
        if (!props.hasLastname) {
            questions.value.unshift({
                type: "text",
                icon: Type,
                title: t("event_types.booking_form.questions.last_name.title"),
                description: t(
                    "event_types.booking_form.questions.last_name.description",
                ),
                predefined: "last_name",
            });
        } else {
            questions.value = questions.value.filter(
                (question) => question.predefined !== "last_name",
            );
        }
    },
);

watch(
    () => props.hasPhone,
    () => {
        if (!props.hasPhone) {
            questions.value.unshift({
                predefined: "phone",
                type: "text",
                icon: PhoneCall,
                title: t("event_types.booking_form.questions.phone.title"),
                description: t(
                    "event_types.booking_form.questions.phone.description",
                ),
            });
        } else {
            questions.value = questions.value.filter(
                (question) => question.predefined !== "phone",
            );
        }
    },
);

function addElement(question: Question) {
    expanded.value = false;
    emit("add-custom-field", question);
}

const questions = ref<Question[]>([
    {
        type: "text",
        icon: Type,
        title: t("event_types.booking_form.questions.text.title"),
        description: t("event_types.booking_form.questions.text.description"),
    },
    {
        type: "textarea",
        icon: Text,
        title: t("event_types.booking_form.questions.textarea.title"),
        description: t("event_types.booking_form.questions.textarea.title"),
    },
    {
        predefined: "phone",
        type: "phone",
        icon: PhoneCall,
        title: t("event_types.booking_form.questions.phone.title"),
        description: t("event_types.booking_form.questions.phone.description"),
    },
    {
        type: "checkbox",
        icon: CheckSquare,
        title: t("event_types.booking_form.questions.checkbox.title"),
        description: t(
            "event_types.booking_form.questions.checkbox.description",
        ),
    },
    {
        type: "select",
        icon: List,
        title: t("event_types.booking_form.questions.select.title"),
        description: t("event_types.booking_form.questions.select.description"),
    },
    {
        type: "radio",
        icon: CircleDot,
        title: t("event_types.booking_form.questions.radio.title"),
        description: t("event_types.booking_form.questions.radio.description"),
    },
    {
        type: "multiselect",
        icon: ListChecks,
        title: t("event_types.booking_form.questions.multiselect.title"),
        description: t(
            "event_types.booking_form.questions.multiselect.description",
        ),
    },
]);

onMounted(() => {
    if (!props.hasFirstname) {
        questions.value.unshift({
            type: "text",
            icon: Type,
            title: t("event_types.booking_form.questions.first_name.title"),
            description: t(
                "event_types.booking_form.questions.first_name.description",
            ),
            predefined: "first_name",
        });
    }

    if (!props.hasLastname) {
        questions.value.unshift({
            type: "text",
            icon: Type,
            title: t("event_types.booking_form.questions.last_name.title"),
            description: t(
                "event_types.booking_form.questions.last_name.description",
            ),
            predefined: "last_name",
        });
    }
});
</script>
